import React, { useMemo, useState } from 'react';
import Badge from '../../../components/ui/Badge';
import { ChevronDownIcon } from '../../../components/ui';
import { useTheme } from '../../../contexts/ThemeContext';
import { cn } from '../../../lib/utils';
import { ReferenceDataGeneralList, type ReferenceInboxColumn } from '../ReferenceDataGeneralList';
import { moduleConfig } from './config';
import { useCrewWageColumns, type CrewContractColumn } from './useData';

const COLUMN_TYPE_LABEL: Record<string, string> = {
  EARNING: 'Earning',
  DEDUCTION: 'Deduction',
  INFO: 'Info',
};

const List: React.FC = () => {
  const { colors, theme } = useTheme();
  const isDark = theme === 'dark';
  const formatDate = moduleConfig.utils.formatDate;
  const [columnType, setColumnType] = useState('');

  const columns = useMemo<ReferenceInboxColumn<CrewContractColumn>[]>(
    () => [
      {
        key: 'code',
        header: 'Code',
        className: 'whitespace-nowrap',
        render: (row) => <span className="font-medium font-mono text-xs tracking-wide">{row.code}</span>,
      },
      {
        key: 'name',
        header: 'Name',
        render: (row) => row.name,
      },
      {
        key: 'columnType',
        header: 'Type',
        className: 'hidden whitespace-nowrap sm:table-cell',
        render: (row) => COLUMN_TYPE_LABEL[row.columnType] ?? row.columnType,
      },
      {
        key: 'dataType',
        header: 'Data',
        className: 'hidden whitespace-nowrap md:table-cell',
        render: (row) => row.dataType,
      },
      {
        key: 'includeInTotal',
        header: 'In total',
        className: 'hidden whitespace-nowrap lg:table-cell',
        render: (row) => (
          <Badge variant={row.includeInTotal ? 'success' : 'secondary'}>
            {row.includeInTotal ? 'Yes' : 'No'}
          </Badge>
        ),
      },
      {
        key: 'isActive',
        header: 'Active',
        className: 'whitespace-nowrap',
        render: (row) => (
          <Badge variant={row.isActive ? 'success' : 'danger'}>{row.isActive ? 'Yes' : 'No'}</Badge>
        ),
      },
      {
        key: 'sortOrder',
        header: 'Order',
        className: 'hidden whitespace-nowrap xl:table-cell',
        render: (row) => row.sortOrder,
      },
      {
        key: 'createdAt',
        header: 'Created',
        className: 'hidden whitespace-nowrap xl:table-cell',
        render: (row) => formatDate(row.createdAt),
      },
    ],
    [formatDate],
  );

  const extraFilters = useMemo(
    () => (columnType ? { columnType: [columnType] } : undefined),
    [columnType],
  );

  return (
    <ReferenceDataGeneralList
      moduleConfig={moduleConfig}
      useData={useCrewWageColumns}
      columns={columns}
      defaultSort={{ key: 'sortOrder', direction: 'asc' }}
      newLabel="New wage column"
      getRowLabel={(row) => row.name}
      extraFilters={extraFilters}
      hasExtraFilters={Boolean(columnType)}
      onClearExtraFilters={() => setColumnType('')}
      tabTrailing={
        <>
          <span
            className={cn('mx-1 my-1.5 w-px shrink-0 self-stretch', isDark ? 'bg-white/15' : 'bg-slate-300/80')}
            aria-hidden
          />
          <label
            className={cn(
              'relative inline-flex items-center rounded-lg transition',
              columnType
                ? cn(colors.surface, 'shadow-sm ring-1', isDark ? 'ring-white/10' : 'ring-black/5')
                : colors.interactiveHover,
            )}
          >
            <span className="sr-only">Filter by type</span>
            <select
              value={columnType}
              onChange={(e) => setColumnType(e.target.value)}
              className={cn(
                'appearance-none cursor-pointer rounded-lg border-0 bg-transparent py-2 pl-3 pr-8 text-sm font-medium outline-none',
                'focus:ring-2 focus:ring-indigo-500/25',
                columnType ? colors.text : colors.textMuted,
              )}
              title="Filter by type"
            >
              <option value="">All types</option>
              <option value="EARNING">Earning</option>
              <option value="DEDUCTION">Deduction</option>
              <option value="INFO">Info</option>
            </select>
            <ChevronDownIcon
              className={cn(
                'pointer-events-none absolute right-2 h-3.5 w-3.5',
                columnType ? colors.primaryFg : 'opacity-50',
              )}
            />
          </label>
        </>
      }
    />
  );
};

export default List;
